import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../layout/Layout';
import axios from 'axios';

interface ContactRequest {
  id: number;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
  ip_address: string | null;
}

const ContactRequests: React.FC = () => {
  const [requests, setRequests] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedRequest, setSelectedRequest] = useState<ContactRequest | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.post('/admin/website/contact-requests', {
        draw: currentPage,
        start: (currentPage - 1) * 10,
        length: 10,
        search: { value: '' },
        order: [{ column: 5, dir: 'desc' }],
        filters: { status: statusFilter }
      });

      if (response.data.data) {
        setRequests(response.data.data);
        setTotalRecords(response.data.recordsTotal);
        setTotalPages(Math.ceil(response.data.recordsFiltered / 10));
      }
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const loadRequestDetails = async (id: number) => {
    try {
      const response = await axios.get(`/admin/website/contact-requests/${id}`);
      if (response.data.success) {
        setSelectedRequest(response.data.data.request);
      }
    } catch (error) {
      console.error('Error loading request details:', error);
    }
  };

  const handleStatusUpdate = async (id: number, newStatus: string) => {
    try {
      await axios.put(`/admin/website/contact-requests/${id}/status`, {
        status: newStatus
      });
      await loadRequests();
      if (selectedRequest && selectedRequest.id === id) {
        await loadRequestDetails(id);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'warning',
      responded: 'info',
      resolved: 'success'
    };
    return <span className={`badge bg-${colors[status] || 'secondary'}`}>{status}</span>;
  };

  return (
    <Layout>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Contact Requests</h4>
              <p className="text-muted mb-0">Manage contact form submissions from website</p>
            </div>
          </div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
            <li className="breadcrumb-item">Website</li>
            <li className="breadcrumb-item active">Contact Requests</li>
          </ol>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                  <label className="form-label me-2">Filter by Status:</label>
                  <select
                    className="form-select d-inline-block"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    style={{ maxWidth: '200px' }}
                  >
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="responded">Responded</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                <div>
                  <small className="text-muted">Total: {totalRecords}</small>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status"></div>
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">No requests found</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped table-bordered">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Subject</th>
                          <th>Status</th>
                          <th>Created At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map((request) => (
                          <tr key={request.id}>
                            <td>{request.id}</td>
                            <td>{request.name}</td>
                            <td>{request.email}</td>
                            <td>{request.subject}</td>
                            <td>{getStatusBadge(request.status)}</td>
                            <td>{formatDate(request.created_at)}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-primary me-1"
                                onClick={() => loadRequestDetails(request.id)}
                              >
                                View
                              </button>
                              {request.status === 'pending' && (
                                <button
                                  className="btn btn-sm btn-info me-1"
                                  onClick={() => handleStatusUpdate(request.id, 'responded')}
                                >
                                  Mark Responded
                                </button>
                              )}
                              {request.status !== 'resolved' && (
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => handleStatusUpdate(request.id, 'resolved')}
                                >
                                  Mark Resolved
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Previous
                      </button>
                      <span className="mx-2">Page {currentPage} of {totalPages}</span>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={currentPage >= totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedRequest && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Contact Request Details</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedRequest(null)}></button>
              </div>
              <div className="modal-body">
                <p><strong>Name:</strong> {selectedRequest.name}</p>
                <p><strong>Email:</strong> {selectedRequest.email}</p>
                <p><strong>Subject:</strong> {selectedRequest.subject}</p>
                <p><strong>Status:</strong> {getStatusBadge(selectedRequest.status)}</p>
                <p><strong>Created At:</strong> {formatDate(selectedRequest.created_at)}</p>
                {selectedRequest.ip_address && (
                  <p><strong>IP Address:</strong> {selectedRequest.ip_address}</p>
                )}
                <div className="mt-3">
                  <strong>Message:</strong>
                  <div className="border p-3 mt-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                    {selectedRequest.message}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setSelectedRequest(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ContactRequests;

